/**
 * Compresión de imágenes para web.
 * Misma lógica que useAdvancedImagePicker en mobile: redimensionar por maxWidth/maxHeight,
 * comprimir JPEG con quality, y si excede maxFileKB reintentar con menor calidad y tamaño.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxFileKB?: number;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.8,
  maxFileKB: 500,
};

function loadImage(src: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = document.createElement("img");
    img.crossOrigin = "anonymous";
    const url = typeof src === "string" ? src : URL.createObjectURL(src);
    img.onload = () => {
      if (typeof src !== "string") URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      if (typeof src !== "string") URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Comprime una imagen (Blob o Data URL) usando canvas.
 * Redimensiona si supera maxWidth/maxHeight, comprime JPEG con quality.
 * Si el resultado supera maxFileKB, reintenta hasta 3 veces reduciendo calidad y tamaño.
 */
export async function compressImage(
  source: Blob | string,
  options: CompressionOptions = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const dataUrl = typeof source === "string" ? source : await blobToDataURL(source);
  const img = await loadImage(dataUrl);
  let width = img.naturalWidth;
  let height = img.naturalHeight;

  const scaleW = opts.maxWidth / width;
  const scaleH = opts.maxHeight / height;
  const scale = Math.min(scaleW, scaleH, 1);
  if (scale < 1) {
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
  }

  let quality = opts.quality;
  let currentWidth = width;
  let currentHeight = height;
  const targetBytes = opts.maxFileKB * 1024;
  let attempt = 0;

  const draw = (w: number, h: number, q: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas 2d not available"));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
        "image/jpeg",
        q
      );
    });
  };

  let blob = await draw(currentWidth, currentHeight, quality);

  while (blob.size > targetBytes && attempt < 3 && currentWidth > 0 && currentHeight > 0) {
    attempt += 1;
    quality = Math.max(0.5, quality - 0.15);
    currentWidth = Math.floor(currentWidth * 0.85);
    currentHeight = Math.floor(currentHeight * 0.85);
    blob = await draw(currentWidth, currentHeight, quality);
  }

  return blob;
}

/**
 * Obtiene un Blob desde el portapapeles (evento paste).
 * Devuelve el primer item de tipo image si existe.
 */
export function getImageBlobFromClipboard(event: ClipboardEvent): Blob | null {
  const items = event.clipboardData?.items;
  if (!items) return null;
  for (const item of items) {
    if (item.type.startsWith("image/")) {
      const blob = item.getAsFile();
      return blob ? blob : null;
    }
  }
  return null;
}
